import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Stack,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import type { PayoutInvoiceListItem, PayoutInvoiceStatus } from '@/types/payout';
import { CopyableText } from '@/utils/copy';
import { maskKeepEdges } from '@/utils/mask';
import { parseRecipientDetailsMasked } from '@/utils/payout';
import { PAYOUT_INVOICE_STATUS_LABELS } from '../constants';
import RecipientMaskedCell from './RecipientMaskedCell';
import PayoutVideoButton from './PayoutVideoButton';
import PayoutReceiptButton from './PayoutReceiptButton';
import WorkClaimButton from '@/components/WorkClaimButton';
import { payoutsService } from '@/services';
import { useNotification } from '@/contexts/NotificationContext';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { RoleEnum } from '@/types/auth/roles';

function formatCreatedAt(dateCreate: string): { date: string; time: string } {
  const d = new Date(dateCreate);
  return {
    date: d.toLocaleDateString('ru-RU'),
    time: d.toLocaleTimeString('ru-RU'),
  };
}

function formatConfirmationDuration(startedAt: string, now: number): string {
  const startedAtMs = new Date(startedAt).getTime();
  if (!Number.isFinite(startedAtMs)) return '-';

  const totalSeconds = Math.max(0, Math.floor((now - startedAtMs) / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const time = [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');

  return days > 0 ? `${days} д ${time}` : time;
}

const getStatusChipColor = (
  status: PayoutInvoiceStatus,
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'SUCCESS':
      return 'success';
    case 'DECLINED':
      return 'error';
    case 'UNDER_REVIEW':
      return 'info';
    case 'AVAILABLE':
      return 'default';
    case 'ACCEPTED':
    case 'IN_PROGRESS':
      return 'warning';
    default:
      return 'default';
  }
};

interface PayoutInvoicesTableProps {
  invoices: PayoutInvoiceListItem[];
  loading: boolean;
  error: string | null;
  /** Колонка «Действия» (например вкладка «Подтверждение»). */
  renderRowActions?: (row: PayoutInvoiceListItem) => React.ReactNode;
  /** Если false — ID показывается целиком без маски. */
  maskId?: boolean;
  onClaimChanged?: () => void | Promise<void>;
  allowClaim?: boolean;
  showConfirmationDuration?: boolean;
}

const PayoutInvoicesTable: React.FC<PayoutInvoicesTableProps> = ({
  invoices,
  loading,
  error,
  renderRowActions,
  maskId = true,
  onClaimChanged = () => undefined,
  allowClaim = false,
  showConfirmationDuration = false,
}) => {
  const { showNotification } = useNotification();
  const [now, setNow] = useState(() => Date.now());
  const user = useSelector((state: RootState) => state.auth.user);
  const canResetWork = Boolean(
    user?.is_superuser || user?.role === RoleEnum.ADMIN || user?.username?.toLowerCase() === 'support_ilya',
  );
  const attributeColumnNames = useMemo(() => {
    const seen = new Set<string>();
    for (const invoice of invoices) {
      const parsed = parseRecipientDetailsMasked(invoice.recipient_details_masked);
      if (parsed === null || typeof parsed === 'string' || !Array.isArray(parsed.attributes)) {
        continue;
      }
      for (const attr of parsed.attributes) {
        if (!seen.has(attr.name)) {
          seen.add(attr.name);
        }
      }
    }
    return Array.from(seen);
  }, [invoices]);

  useEffect(() => {
    if (!showConfirmationDuration) return undefined;

    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [showConfirmationDuration]);

  const colCount =
    10 +
    attributeColumnNames.length +
    (showConfirmationDuration ? 1 : 0) +
    (renderRowActions ? 1 : 0);

  return (
    <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
      <Table sx={{ minWidth: 1180 }}>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Сумма</TableCell>
            <TableCell>ПС</TableCell>
            <TableCell>Реквизиты</TableCell>
            {attributeColumnNames.map((name) => (
              <TableCell key={name}>{name}</TableCell>
            ))}
            <TableCell>Трейдер</TableCell>
            <TableCell>Создана</TableCell>
            {showConfirmationDuration ? <TableCell>В подтверждении</TableCell> : null}
            <TableCell>Видео</TableCell>
            <TableCell>Чек</TableCell>
            <TableCell>Ответственный</TableCell>
            {renderRowActions ? <TableCell align="right">Действия</TableCell> : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <CircularProgress />
                  <Typography variant="body2" color="textSecondary">
                    Загрузка данных...
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ width: '100%', maxWidth: 500 }}>
                  {error}
                </Alert>
              </TableCell>
            </TableRow>
          ) : invoices.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  Нет данных для отображения
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            invoices.map((row) => {
              const created = formatCreatedAt(row.date_create);
              const parsedMasked = parseRecipientDetailsMasked(row.recipient_details_masked);
              const attributesByName =
                parsedMasked !== null && typeof parsedMasked !== 'string' && Array.isArray(parsedMasked.attributes)
                  ? new Map(parsedMasked.attributes.map((attr) => [attr.name, attr.value]))
                  : new Map<string, string>();
              return (
                <TableRow key={row.id}>
                  <TableCell sx={maskId ? undefined : { whiteSpace: 'nowrap' }}>
                    <CopyableText
                      value={row.id}
                      displayValue={maskId ? maskKeepEdges(row.id) : undefined}
                      copiedMessage="ID заявки скопирован"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={PAYOUT_INVOICE_STATUS_LABELS[row.status]}
                      color={getStatusChipColor(row.status)}
                      size="small"
                      sx={{ minWidth: 120, justifyContent: 'center' }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, wordBreak: 'break-all' }}>{row.initial_amount}</TableCell>
                  <TableCell>{row.payment_system_token}</TableCell>
                  <TableCell sx={{ maxWidth: 300, verticalAlign: 'top' }}>
                    <RecipientMaskedCell value={row.recipient_details_masked} />
                  </TableCell>
                  {attributeColumnNames.map((name) => (
                    <TableCell key={`${row.id}-${name}`}>{attributesByName.get(name) ?? '-'}</TableCell>
                  ))}
                  <TableCell>{row.trader_name}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Stack spacing={0.25} alignItems="flex-start">
                      <Typography variant="body2" component="span">
                        {created.date}
                      </Typography>
                      <Typography variant="body2" component="span" color="text.secondary">
                        {created.time}
                      </Typography>
                    </Stack>
                  </TableCell>
                  {showConfirmationDuration ? (
                    <TableCell sx={{ minWidth: 150, verticalAlign: 'top' }}>
                      {row.confirmation_started_at ? (
                        <Stack spacing={0.25} alignItems="flex-start">
                          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                            {formatConfirmationDuration(row.confirmation_started_at, now)}
                          </Typography>
                          <Typography variant="caption" component="span" color="text.secondary">
                            с {new Date(row.confirmation_started_at).toLocaleString('ru-RU')}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <PayoutVideoButton payoutId={row.id} hasVideo={row.has_video} />
                  </TableCell>
                  <TableCell>
                    <PayoutReceiptButton payoutId={row.id} hasReceipt={Boolean(row.has_receipt)} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 170 }}>
                    <WorkClaimButton
                      claim={row}
                      onClaim={() => payoutsService.claimPayoutInvoice(row.id)}
                      onRelease={() => payoutsService.releasePayoutInvoice(row.id)}
                      onAdminReset={() => payoutsService.resetPayoutInvoiceWorkState(row.id)}
                      canAdminReset={canResetWork}
                      allowClaim={allowClaim}
                      onChanged={onClaimChanged}
                      onError={(error) => {
                        const detail = (error as any)?.response?.data?.detail;
                        showNotification(detail || 'Не удалось изменить ответственного', 'error');
                      }}
                    />
                  </TableCell>
                  {renderRowActions ? (
                    <TableCell align="right" sx={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      {renderRowActions(row)}
                    </TableCell>
                  ) : null}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PayoutInvoicesTable;
